import React from 'react'
import { observable, action, computed } from 'mobx'
import { post } from 'axios'
import moment from 'moment'
import { omit, find as _find, filter, set, pick, debounce, defaults } from 'lodash'
import store from 'store'
import { get } from 'react-global-configuration'
import logo from '../../static/logo-stripe.png'
import { searchFlights, AIRLINES_FILTER_TYPE } from 'skypicker'
// import CoinbaseCommerce from '../components/CoinbaseCommerce'
import isEmail from 'validator/lib/isEmail'
import userStore from './UserStore'

const ENV = key => {
  try {
    return get(key)
  } catch (e) {
    return {}
  }
}

if (typeof window !== 'undefined') {
  window.gtag = window.gtag || (() => {})
}

let stripeHandler = {}
let stripeV3 = {}
try {
  stripeHandler = StripeCheckout.configure({
    key: ENV('stripePK'),
    locale: 'auto',
  })
  stripeV3 = Stripe(ENV('stripePK'), { betas: ['checkout_beta_4'] })
} catch (e) {
  console.warn(e)
}

class FlightStore {
  DATE_FORMAT = 'DD MMM, YYYY'
  PRICE = 500
  freshChatWidget = null
  @observable loading = false
  @observable err = false

  @observable from = null
  @observable to = null
  @observable when = moment()
    .add(20, 'days')
    .format(this.DATE_FORMAT)
  @observable returnDate = moment()
    .add(40, 'days')
    .format(this.DATE_FORMAT)
  @observable currencyCode = 'USD'
  @observable isRoundTrip: boolean = false
  @observable firstName = null
  @observable lastName = null
  @observable email = null
  @observable pnr = null
  @observable extraTravelers = []
  @observable selectedFlight = null
  @observable token = null
  @observable referralCode = null
  @observable promoCode = null

  @observable showPNR = false
  @observable flights = []
  @observable ticketIssued = false
  @observable downloadLink = false
  @observable pastTickets = []
  @observable coinbaseChargeCode = null
  @observable routeStats = null

  constructor() {
    this.firstName = store.get('firstName')
    this.lastName = store.get('lastName')
    this.email = store.get('email')
    this.currencyCode = store.get('currencyCode') || 'USD'
    this.pastTickets = store.get('pastTickets') || []
  }

  @action handleChange = (e, { name, value, checked }) => {
    const { val } = defaults({ val: value }, { val: checked }, { val: null })
    set(this, name, val)
    if (',firstName,lastName,email,currencyCode'.indexOf(name)) {
      store.set(name, this[name])
    }
    this.updateFreshChatProps(name, this[name])
    // if (name === 'email' && isEmail(this.email || '') && this.firstName) {
    //   this.dropOffFollowUp()
    // }
  }

  @action searchFlights = async () => {
    const that = this
    let { from, to } = this
    if (!from) from = 'SIN'
    if (!to) to = 'DPS'
    that.loading = true
    that.err = null
    this.selectedFlight = null
    window.gtag('event', 'from', {
      event_category: 'Flight Search',
      event_label: from,
    })
    window.gtag('event', 'to', {
      event_category: 'Flight Search',
      event_label: to,
    })
    window.gtag('event', 'from-to', {
      event_category: 'Flight Search',
      event_label: `${from}-${to}`,
    })
    const isPastDateTrip = moment(this.when).diff(Date.now()) < 0
    const when = (() => {
      if (isPastDateTrip) {
        return moment()
          .add(2, 'day')
          .format(this.DATE_FORMAT)
      }
      return this.when
    })()
    const returnDate = (() => {
      if (isPastDateTrip) {
        const daysBetween = moment(this.returnDate).diff(moment(this.when), 'days')
        return moment()
          .add(2, 'day')
          .add(daysBetween, 'day')
          .format(this.DATE_FORMAT)
      }
      return this.returnDate
    })()

    const returnDepartureDateTimeRange = (() => {
      if (Boolean(that.isRoundTrip) && that.returnDate) {
        return {
          days: {
            start: moment(returnDate)
              // .add(1, 'month')
              .format('YYYY-MM-DD'),
            end: moment(returnDate)
              // .add(1, 'month')
              .format('YYYY-MM-DD'),
          },
          timeOfDay: {
            start: '00:01',
            end: '23:59',
          },
        }
      } else {
        return null
      }
    })()

    this.fetchRouteStats({ from, to })
    try {
      const { data } = await post(`${ENV('apiDomain')}/ticket/flights`, {
        from,
        to,
        start: when,
        end: when,
        whenDate: when,
        returnDepartureDateTimeRange: returnDepartureDateTimeRange,
        isRoundTrip: that.isRoundTrip,
      })

      that.flights = data
      that.loading = false
      if (data.length === 0) {
        this.err = {
          message: `No flights found for your seach query`,
          description: `Try different airports or departure dates`,
        }
      }
    } catch (err) {
      this.catchError(err)

      window.gtag('event', 'OLD selectFlight was used', {
        event_category: 'Flight Search',
      })

      searchFlights({
        departureIdentifier: from,
        arrivalIdentifier: to,
        departureDateTimeRange: {
          days: {
            start: moment(when, this.DATE_FORMAT).format('YYYY-MM-DD'),
            end: moment(when, this.DATE_FORMAT).format('YYYY-MM-DD'),
          },
          timeOfDay: {
            start: '00:01',
            end: '23:55',
          },
        },
        returnDepartureDateTimeRange: returnDepartureDateTimeRange,
        passengerCount: 1,
        currencyCode: that.currencyCode,
        airlinesFilter: {
          airlines: ['IX', 'QZ', 'AK', 'D7'],
          type: AIRLINES_FILTER_TYPE.EXCLUDE,
        },
        partner: 'skypicker',
        sortType: 'DURATION', // QUALITY, PRICE, DURATION, DATE
        limit: 200,
      })
        .then(res => {
          that.flights = res.data
          that.loading = false
        })
        .catch(this.catchError)
    }
  }

  @action fetchRouteStats = async ({ from, to }) => {
    const { data } = await post(`${ENV('apiDomain')}/triplog/aggregate`, { flyFrom: to })

    if (data.rate && data.rate < 0.5 && data.failures > 3) {
      this.routeStats = data
      window.gtag('event', 'highRiskWarningShown', {
        event_category: 'Flight Search',
        event_label: to,
      })
    } else {
      this.routeStats = null
    }
  }

  @action selectFlight = id => {
    this.selectedFlight = id
    window.gtag('event', 'selectFlight', { event_category: 'Flight Search' })
  }

  /**
   * @returns {{
   *   from: string,
   *   to: string,
   *   email: string,
   *   when: string,
   *   returnDate: string,
   *   firstName: string,
   *   lastName: string,
   *   pnr: string,
   *   extraTravelers: Array,
   *   selectedFlight: string,
   *   token: Object,
   *   flight: Object,
   *   referralCode: string,
   *   promoCode: string,
   *   coinbaseChargeCode: string,
   *   currencyCode: string
   * }}
   */
  @action getTicketData = _ => {
    const data = pick(this, [
      'from',
      'to',
      'email',
      'when',
      'returnDate',
      'firstName',
      'lastName',
      'pnr',
      'extraTravelers',
      'selectedFlight',
      'token',
      'flight',
      'referralCode',
      'promoCode',
      'coinbaseChargeCode',
      'currencyCode',
    ])
    data.flight = _find(this.flights, f => f.id === this.selectedFlight)
    return data
  }

  @action beforePayment = async () => {
    try {
      const data = this.getTicketData()
      await post(`${ENV('apiDomain')}/ticket/beforePayment`, data)
    } catch (err) {
      this.err = err
      return
    }
  }

  @action generateTicket = async () => {
    this.loading = true
    const data = this.getTicketData()
    window.gtag('event', 'generateTicket', { event_category: 'Flight Search' })
    try {
      const res = await post(`${ENV('apiDomain')}/ticket/issue`, data)
      const { id, uuid } = res.data
      this.downloadLink = `${ENV('apiDomain')}/ticket/pdf?uuid=${uuid}`
      this.ticketIssued = {
        id,
        uuid,
        selectedFlight: this.selectedFlight,
      }
      this.pastTickets.push(this.ticketIssued)
      store.set('pastTickets', this.pastTickets)
      if (this.promoCode) this.promoCode = null
      this.err = false
      if (this.freshChatWidget) {
        this.freshChatWidget.user.setProperties({ purchased: true })
      }
    } catch (err) {
      console.warn('generateTicket', err, err.response)
      this.err = err
      if (err.response && err.response.data.indexOf('promo code')) {
        this.promoCode = null
        this.token = null
        // this.buy({skipStripeElements: true})
        // } else {
        // this.buyV2()
      }
    } finally {
      this.loading = false
    }
  }

  @action buy = async () => {
    if (!this.email) return alert('Please enter your email')
    if (!isEmail(this.email)) return alert('Please enter a valid email')
    this.loading = true
    this.err = false
    const { user } = userStore
    if (user && user.balance > this.totalPrice) {
      window.gtag('event', 'buy with balance', { event_category: 'Flight Search' })
      await this.generateTicket()
      userStore.fetchMe()
      return
    }
    if (this.promoCode) {
      window.gtag('event', 'buy with promo code', { event_category: 'Flight Search' })
      return this.generateTicket()
    }

    stripeHandler.open({
      // name: 'PDF',
      email: this.email,

      image: logo,
      description: `PDF GENERATOR`,
      // zipCode: true,
      rememberMe: false,
      amount: this.totalPrice,
      token: (token, args) => {
        this.token = token
        this.generateTicket()
        this.trackConversion({ id: this.token.id, totalPrice: this.totalPrice })
      },
      closed: (a, b, c) => {
        if (!this.token) {
          this.loading = false
          window.gtag('event', 'did not want to pay', { event_category: 'ecommerce' })
          window.gtag('event', 'did not want to pay', { event_category: 'general' })
        }
      },
    })
  }

  @action buyV2 = async () => {
    if (!this.email) return
    this.loading = true
    this.err = false
    window.gtag('event', 'buy', { event_category: 'Flight Search' })
    if (this.promoCode) {
      window.gtag('event', 'buy with promo code', { event_category: 'Flight Search' })
      return this.generateTicket()
    }

    let uuid = ''
    try {
      const data = omit(this, [
        'loading',
        'flights',
        'ticketIssued',
        'routeStats',
        'downloadLink',
        'DATE_FORMAT',
      ])
      data.flight = _find(this.flights, f => f.id === this.selectedFlight)
      const res = await post(`${ENV('apiDomain')}/ticket/beforePayment`, data)
      uuid = res.data.uuid
    } catch (err) {
      this.err = err
      this.loading = false
      return
    }

    stripeV3
      .redirectToCheckout({
        items: [{ sku: ENV('stripeSKU'), quantity: this.totalQuantity }],
        // Note that it is not guaranteed your customers will be redirected to this
        // URL *100%* of the time, it's possible that they could e.g. close the
        // tab between form submission and the redirect.
        // successUrl: 'https://onwardticketmonk.com/checkout-complete',
        // TODO location.origin is not safe to use! Improve security here
        successUrl: `${location.origin}/checkout-complete?uuid=${uuid}`,
        cancelUrl: `${location.origin}`,
        customerEmail: this.email,
      })
      .then(result => {
        window.gtag('event', 'did not want to pay', {
          event_category: 'ecommerce',
        })
        window.gtag('event', 'did not want to pay', {
          event_category: 'general',
        })
        this.loading = false
        this.catchError(result.error)
        console.log('buyV2 result:', result)
        if (result.error) {
          console.warn(result.error.message)
        }
      })
      .catch(err => {
        this.loading = false
        this.catchError(err)
      })
  }

  @action stripeCheckoutEmbedded = async () => {
    if (!this.email) return alert('Please enter your email')
    if (!isEmail(this.email)) return alert('Please enter a valid email')
    this.loading = true
    this.err = false
    window.gtag('event', 'buy', { event_category: 'Crypto Payment' })

    try {
      const data = this.getTicketData()
      const checkout = await stripeV3.initEmbeddedCheckout({
        fetchClientSecret: async () => {
          const res = await post(
            `${ENV('apiDomain')}/ticket/stripeCheckoutEmbedded`,
            data
          )
          return res.data.client_secret
        },
        onPaymentSuccess: params => {
          console.log('onPaymentSuccess', params)
          alert('Payment successful')
        },
      })
      checkout.mount('#stripeCheckoutEmbedded')
    } catch (err) {
      this.err = err
      console.error(err)
    } finally {
      this.loading = false
    }
  }

  @action stripeCheckoutHosted = async () => {
    if (!this.email) return alert('Please enter your email')
    if (!isEmail(this.email)) return alert('Please enter a valid email')
    this.loading = true
    this.err = false
    window.gtag('event', 'buy', { event_category: 'Crypto Payment' })

    try {
      const data = this.getTicketData()
      const res = await post(`${ENV('apiDomain')}/ticket/stripeCheckout`, data)
      window.location.href = res.data.redirectUrl
    } catch (err) {
      this.err = err
      console.error(err)
    } finally {
      this.loading = false
    }
  }

  @action buyWithCrypto = async () => {
    if (!this.email) return alert('Please enter your email')
    if (!isEmail(this.email)) return alert('Please enter a valid email')
    this.loading = true
    this.err = false
    window.gtag('event', 'buy', { event_category: 'Flight Search' })
    window.gtag('event', 'buy', { event_category: 'Crypto Payment' })

    try {
      const data = this.getTicketData()
      const res = await post(`${ENV('apiDomain')}/ticket/initCryptoPayment`, data)
      // this.trackConversion({ id: charge.code, totalPrice: this.totalPrice })
      window.location.href = res.data.redirectUrl
    } catch (err) {
      this.err = err
      console.error(err)
    } finally {
      this.loading = false
    }
  }

  @action buyWithPlisio = async () => {
    if (!this.email) return alert('Please enter your email')
    if (!isEmail(this.email)) return alert('Please enter a valid email')
    this.loading = true
    this.err = false
    window.gtag('event', 'buy', { event_category: 'Flight Search' })
    window.gtag('event', 'buy', { event_category: 'Crypto Payment' })

    try {
      const data = this.getTicketData()
      const res = await post(`${ENV('apiDomain')}/ticket/initPlisioPayment`, data)
      if (!res.data.redirectUrl)
        throw new Error('Something went wrong. Try a different payment')
      // this.trackConversion({ id: charge.code, totalPrice: this.totalPrice })
      window.location.href = res.data.redirectUrl
    } catch (err) {
      this.err = err
      console.error(err)
    } finally {
      this.loading = false
    }
  }

  @action buyWithCoinbaseCommerce = () => {
    this.loading = true
    this.err = false
    window.gtag('event', 'buy', { event_category: 'Flight Search' })
    window.gtag('event', 'buy', { event_category: 'Crypto Payment' })

    let checkoutId = ENV('coinbaseCommerce')[(this.totalPrice / 100) | 0]

    this.beforePayment() // moved here to increase success chances
    CoinbaseCommerce.open({
      checkoutId: checkoutId || ENV('coinbaseCommerceCheckoutId'),
      customMetadata: JSON.stringify({
        email: this.email,
        selectedFlight: this.selectedFlight,
      }),
      onPaymentDetected: ({ code }) => {
        // TODO this might not get executed
        this.loading = true
        if (code) {
          this.coinbaseChargeCode = code
          // this.beforePayment()
        }
        console.log('onPaymentDetected', code)
        window.gtag('event', 'payment detected', {
          event_category: 'Crypto Payment',
        })
      },
      onChargeSuccess: charge => {
        this.loading = false
        this.token = charge
        this.generateTicket()
        this.trackConversion({
          id: charge.code,
          totalPrice: this.totalPrice,
        })
        window.gtag('event', 'success', {
          event_category: 'Crypto Payment',
          ...charge,
        })
        console.log('onChargeSuccess', charge)
      },
      onChargeFailure: fail => {
        this.loading = false
        window.gtag('event', 'failed', {
          event_category: 'Crypto Payment',
          ...fail,
        })
        console.log('onChargeFailure', fail)
      },
      onModalClosed: () => {
        this.loading = false
        window.gtag('event', 'modal closed', {
          event_category: 'Crypto Payment',
        })
        console.log('onModalClosed')
      },
    })
  }

  @action addTraveler = (type = 'Adult') => {
    this.extraTravelers.push({
      firstName: '',
      lastName: '',
      type: type,
    })
  }

  @action removeTraveler = (type = 'Adult') => {
    let travelers = this.extraTravelers
    travelers.pop()
    this.extraTravelers = travelers
  }

  @action togglePNR = () => {
    this.showPNR = !this.showPNR
  }

  @computed get travelersQty() {
    return {
      adults: 1 + filter(this.extraTravelers, { type: 'Adult' }).length,
      children: filter(this.extraTravelers, { type: 'Child' }).length,
    }
  }

  @computed get totalPrice() {
    return this.PRICE * this.totalQuantity * (this.isRoundTrip ? 2 : 1)
  }

  @computed get totalQuantity() {
    return 1 + this.extraTravelers.length
  }

  @computed get currencyOptions() {
    return [
      'AED',
      'AFN',
      'ALL',
      'AMD',
      'ANG',
      'AOA',
      'ARS',
      'AUD',
      'AWG',
      'AZN',
      'BAM',
      'BBD',
      'BDT',
      'BGN',
      'BHD',
      'BIF',
      'BMD',
      'BND',
      'BOB',
      'BRL',
      'BSD',
      'BTC',
      'BTN',
      'BWP',
      'BYR',
      'BZD',
      'CAD',
      'CDF',
      'CHF',
      'CLF',
      'CLP',
      'CNY',
      'COP',
      'CRC',
      'CUC',
      'CUP',
      'CVE',
      'CZK',
      'DJF',
      'DKK',
      'DOP',
      'DZD',
      'EEK',
      'EGP',
      'ERN',
      'ETB',
      'EUR',
      'FJD',
      'FKP',
      'GBP',
      'GEL',
      'GGP',
      'GHS',
      'GIP',
      'GMD',
      'GNF',
      'GTQ',
      'GYD',
      'HKD',
      'HNL',
      'HRK',
      'HTG',
      'HUF',
      'IDR',
      'ILS',
      'IMP',
      'INR',
      'IQD',
      'IRR',
      'ISK',
      'JEP',
      'JMD',
      'JOD',
      'JPY',
      'KES',
      'KGS',
      'KHR',
      'KMF',
      'KPW',
      'KRW',
      'KWD',
      'KYD',
      'KZT',
      'LAK',
      'LBP',
      'LKR',
      'LRD',
      'LSL',
      'LTL',
      'LVL',
      'LYD',
      'MAD',
      'MDL',
      'MGA',
      'MKD',
      'MMK',
      'MNT',
      'MOP',
      'MRO',
      'MTL',
      'MUR',
      'MVR',
      'MWK',
      'MXN',
      'MYR',
      'MZN',
      'NAD',
      'NGN',
      'NIO',
      'NOK',
      'NPR',
      'NZD',
      'OMR',
      'PAB',
      'PEN',
      'PGK',
      'PHP',
      'PKR',
      'PLN',
      'PYG',
      'QAR',
      'QUN',
      'RON',
      'RSD',
      'RUB',
      'RWF',
      'SAR',
      'SBD',
      'SCR',
      'SDG',
      'SEK',
      'SGD',
      'SHP',
      'SLL',
      'SOS',
      'SRD',
      'STD',
      'SVC',
      'SYP',
      'SZL',
      'THB',
      'TJS',
      'TMT',
      'TND',
      'TOP',
      'TRY',
      'TTD',
      'TWD',
      'TZS',
      'UAH',
      'UGX',
      'USD',
      'UYU',
      'UZS',
      'VEF',
      'VND',
      'VUV',
      'WST',
      'XAF',
      'XAG',
      'XAU',
      'XCD',
      'XDR',
      'XOF',
      'XPD',
      'XPF',
      'XPT',
      'YER',
      'ZAR',
      'ZMK',
      'ZMW',
      'ZWL',
    ].map(cur => ({ key: cur, value: cur, text: cur }))
  }

  getDownloadLink = uuid => {
    try {
      return `${ENV('apiDomain')}/ticket/pdf?uuid=${uuid}`
    } catch (err) {
      console.warn('getDownloadLink', err)
      return ''
    }
  }

  trackConversion = ({ id, totalPrice }) => {
    if (!totalPrice) totalPrice = this.PRICE
    window.gtag('event', 'conversion', {
      send_to: 'AW-874844064/j90SCLW4i5UBEKCflKED',
      value: totalPrice / 100,
      currency: 'USD',
      transaction_id: id,
    })
    window.gtag('event', 'purchase', {
      value: totalPrice / 100,
      currency: 'USD',
      transaction_id: id,
    })
  }

  freshChatConfig = {
    onInit: widget => {
      this.freshChatWidget = widget
      this.freshChatWidget.user.setProperties({
        email: this.email,
        first_name: this.firstName,
        last_name: this.lastName,
      })
    },
    token: ENV('freshchat')['token'],
  }

  paypalConfig = {
    client: {
      sandbox: ENV('payPalClientId'),
      production: ENV('payPalClientId'),
    },
    env: ENV('payPalEnv'),
    currency: 'USD',
    shipping: 1,
    style: {
      tagline: false,
      label: 'pay',
      size: 'responsive',
      color: 'gold',
    },
    onSuccess: payment => {
      this.token = { ...payment, amount: this.totalPrice }
      this.generateTicket()
      this.trackConversion({
        id: payment.paymentID,
        totalPrice: this.totalPrice,
      })
      window.gtag('event', 'success', { event_category: 'PayPal Payment' })
      console.log('The payment was succeeded!', payment)
    },
    onCancel: data => {
      this.loading = false
      window.gtag('event', 'cancelled', { event_category: 'PayPal Payment' })
      window.gtag('event', 'did not want to pay', {
        event_category: 'ecommerce',
      })
      window.gtag('event', 'did not want to pay', { event_category: 'general' })
      console.log('The payment was cancelled!', data)
    },
    onError: err => {
      this.loading = false
      window.gtag('event', 'ERR: Paypal script cannot be loaded', {
        event_category: 'PayPal Payment',
      })
      console.warn('PayPal Error!', err)
    },
  }

  dropOffFollowUp = debounce(async () => {
    const data = pick(this, ['email', 'when', 'firstName', 'lastName', 'from', 'to'])
    await post(`${ENV('apiDomain')}/ticket/dropOffFollowUp`, data)
  }, 3000)

  updateFreshChatProps = (name, value) => {
    if (!this.freshChatWidget) {
      return
    }
    if (name === 'email') {
      this.freshChatWidget.user.setEmail(value)
    }
    if (name === 'firstName') {
      this.freshChatWidget.user.setFirstName(value)
    }
    if (name === 'lastName') {
      this.freshChatWidget.user.setLastName(value)
    }
  }

  catchError = err => {
    this.loading = false
    console.warn(err)
  }
}

export default new FlightStore()



// WEBPACK FOOTER //
// ./src/stores/FlightStore.js